import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import { connect } from 'react-redux';
import classNames from 'classnames/bind';
import PropTypes from 'prop-types';

import { Col, ContentLiner, FollowContainer, Grid } from 'bricks';
import PartnerOrderPopup from 'components/PartnerOrderPopup/lazy';
import RecentlyViewedWidget from 'components/RecentlyViewedWidget';
import { MODAL_TYPE } from 'constants/modal';
import { modalSelector } from 'store/common/modal/selectors';
import { isScreenBelow640, isScreenBelow1004 } from 'store/common/settings/breakpoint/selectors';
import { getCatalogFiltersBarOpenState } from 'store/ProductCatalog/controls/selectors';
import { getCatalogFiltersAvailability } from 'store/ProductCatalog/filters/selectors';
import { getRecentlyViewedProductsAvailability } from 'store/widgets/recentlyViewedProducts/selectors';
import commonStyles from 'styles/common.pcss';
import { simulateResize } from 'utils/resize';
import { safeLazy } from 'utils/safeLazy';

import {
  CatalogTakeawayPopupGuard,
  FlocktoryCatalogContainer,
  MainHeadingWidget,
  OutOfStockPopup,
  SidebarPanel,
} from '../../components';
import styles from './styles.pcss';

const ProductDetailsModal = safeLazy(
  () =>
    import(
      /* webpackChunkName: "ProductDetailsModal" */
      '../../components/ProductDetailsModal'
    ),
);
const cx = classNames.bind({ ...commonStyles, ...styles });

class CatalogViewLayout extends Component {
  constructor(props) {
    super(props);
    this.state = { containerNode: null };
  }

  // eslint-disable-next-line react/no-find-dom-node
  setRef = (ref) => this.setState({ containerNode: ReactDOM.findDOMNode(ref) });

  getContainerDOMNode = () => this.state.containerNode;

  /**
   * setRef && getContainerDOMNode used to calculate borders in FollowContainer in Sidebar
   * based on screen size returns number of columns occupied by sidebar
   * size for content block can be calculated as `12 - sidebarSize`
   */
  getSidebarSize = () => {
    const { isMobile, isTablet } = this.props;

    if (isMobile) return 0;
    if (isTablet) return 0;
    return 4;
  };

  render() {
    const {
      bottomChildren,
      children,
      filtersAvailability,
      hasRecentlyViewedProducts,
      id,
      isMobile,
      isOverlayOpen,
      isSimpleView,
      isTablet,
      modalIds,
    } = this.props;

    return (
      <div className={cx('noScroll')}>
        <ContentLiner
          className={cx('padt-s', 'padb-m', { 'padh-s': !isMobile && !isSimpleView }, 'liner')}
          id={id}
          size={[isSimpleView || !filtersAvailability ? 'l' : 'xl']}
        >
          {isSimpleView ?
            children
          : <FlocktoryCatalogContainer>
              <MainHeadingWidget className={cx('marb-xs')} />
              <div className={cx(!isOverlayOpen && 'relative')} ref={this.setRef}>
                {isTablet && filtersAvailability && (
                  <SidebarPanel
                    containerNode={this.getContainerDOMNode()}
                    isMobile={isMobile}
                    isTablet={isTablet}
                  />
                )}
                <Grid>
                  {filtersAvailability ?
                    <Col size={this.getSidebarSize()}>
                      <SidebarPanel containerNode={this.getContainerDOMNode()} />
                    </Col>
                  : null}
                  <Col size={filtersAvailability ? 12 - this.getSidebarSize() : 12}>
                    <FollowContainer
                      containerNode={this.getContainerDOMNode()}
                      isMobile={isMobile}
                      watchResize
                    >
                      <div
                        className={cx('filtersWrapper', isMobile && 'mobile')}
                        onClick={() => requestAnimationFrame(simulateResize)}
                        role="presentation"
                      >
                        {children}
                      </div>
                    </FollowContainer>
                  </Col>
                </Grid>
              </div>
            </FlocktoryCatalogContainer>
          }
        </ContentLiner>
        {React.Children.map(
          [
            ...React.Children.toArray(bottomChildren),
            hasRecentlyViewedProducts && <RecentlyViewedWidget key="recently" />,
          ],
          (child, key) =>
            child && (
              <ContentLiner className={cx('padh-s')} key={+key} size={[isSimpleView ? 'l' : 'xl']}>
                {child}
              </ContentLiner>
            ),
        )}
        <CatalogTakeawayPopupGuard />
        <OutOfStockPopup />
        <PartnerOrderPopup />
        {modalIds.includes(MODAL_TYPE.DETAILS_DEVICE) && <ProductDetailsModal />}
      </div>
    );
  }
}

CatalogViewLayout.propTypes = {
  bottomChildren: PropTypes.node,
  /** own props */
  children: PropTypes.node,
  filtersAvailability: PropTypes.bool,
  hasRecentlyViewedProducts: PropTypes.bool,

  id: PropTypes.string,
  /** redux display type props */
  isMobile: PropTypes.bool,

  /** redux availability props */
  isOverlayOpen: PropTypes.bool,
  isSimpleView: PropTypes.bool,
  isTablet: PropTypes.bool,
};

const mapStateToProps = (state) => ({
  filtersAvailability: getCatalogFiltersAvailability(state),
  hasRecentlyViewedProducts: getRecentlyViewedProductsAvailability(state),
  isMobile: isScreenBelow640(state),
  isOverlayOpen: getCatalogFiltersBarOpenState(state),
  isTablet: isScreenBelow1004(state),
  modalIds: modalSelector(state),
});

export default connect(mapStateToProps)(CatalogViewLayout);
